import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Building2, 
  Search, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar,
  CheckCircle,
  XCircle,
  Eye,
  Trash2,
  Clock,
  Users,
  BarChart3
} from "lucide-react";
import { getAllPharmacies, approvePharmacy, rejectPharmacy, deletePharmacy, getPharmacyStats, Pharmacy } from "../../api/pharmacyService";
import { toast } from "sonner";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { DemoRequestsPage } from "./DemoRequestsPage";

export function PharmaciesPage() {
  const navigate = useNavigate();
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [accountStatusFilter, setAccountStatusFilter] = useState("all");
  const [verifiedSearchTerm, setVerifiedSearchTerm] = useState("");
  const [currentPageAccounts, setCurrentPageAccounts] = useState(1);
  const [currentPageVerified, setCurrentPageVerified] = useState(1);
  const [itemsPerPage] = useState(10);
  const [pharmacyStats, setPharmacyStats] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("verified");

  // Helper function to get the correct ID
  const getPharmacyId = (pharmacy: Pharmacy): string | null => {
    if (pharmacy.id) return pharmacy.id;
    if (pharmacy._id) return pharmacy._id;
    console.error('Pharmacy missing ID:', pharmacy);
    return null;
  };

  // Fetch pharmacies
  const fetchPharmacies = async () => {
    try {
      setLoading(true);
      console.log('PharmaciesPage: Fetching pharmacies from API...');
      
      const [pharmaciesData, statsData] = await Promise.all([
        getAllPharmacies(1, 100), // Fetch more to handle client-side filtering
        getPharmacyStats()
      ]);
      
      console.log('PharmaciesPage: API response:', pharmaciesData);
      console.log('PharmaciesPage: Stats response:', statsData);
      
      if (pharmaciesData.pharmacies && Array.isArray(pharmaciesData.pharmacies)) {
        const pharmacyList = pharmaciesData.pharmacies;
        setPharmacies(pharmacyList);
        
        // Calculate stats from actual data if API stats are empty
        const calculatedStats = {
          totalPharmacies: pharmacyList.length,
          verifiedPharmacies: pharmacyList.filter(p => p.isVerified || p.accountStatus === 'verified').length,
          pendingPharmacies: pharmacyList.filter(p => p.accountStatus === 'pending' || (!p.isVerified && p.accountStatus !== 'verified')).length,
          activePharmacies: pharmacyList.filter(p => p.isActive).length,
          totalRevenue: 0 // Will be calculated from orders if needed
        };
        
        console.log('PharmaciesPage: Calculated stats:', calculatedStats);
        
        // Use API stats if available, otherwise use calculated stats
        const finalStats = (statsData && Object.keys(statsData).length > 0) ? {
          ...calculatedStats,
          ...statsData
        } : calculatedStats;
        
        console.log('PharmaciesPage: Final stats:', finalStats);
        setPharmacyStats(finalStats);
      } else {
        console.warn('PharmaciesPage: Invalid pharmacies data:', pharmaciesData);
        setPharmacies([]);
        toast.warning('Invalid data format received from API');
      }
    } catch (error) {
      console.error('PharmaciesPage: Error fetching pharmacies:', error);
      toast.error('Failed to load pharmacies');
      setPharmacies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPharmacies();
  }, []);

  // Pharmacy management functions
  const approvePharmacyHandler = async (pharmacy: Pharmacy) => {
    const pharmacyId = getPharmacyId(pharmacy);
    if (!pharmacyId) {
      toast.error('Invalid pharmacy ID');
      return;
    }

    try {
      const success = await approvePharmacy(pharmacyId);
      if (success) {
        setPharmacies(prev => 
          prev.map(p => 
            getPharmacyId(p) === pharmacyId 
              ? { ...p, isActive: true, isVerified: true, accountStatus: 'verified' }
              : p
          )
        );
        toast.success('Pharmacy approved successfully');
      } else {
        toast.error('Failed to approve pharmacy');
      }
    } catch (error) {
      console.error('Error approving pharmacy:', error);
      toast.error('Failed to approve pharmacy');
    }
  };

  const rejectPharmacyHandler = async (pharmacy: Pharmacy, notes?: string) => {
    const pharmacyId = getPharmacyId(pharmacy);
    if (!pharmacyId) {
      toast.error('Invalid pharmacy ID');
      return;
    }

    try {
      const success = await rejectPharmacy(pharmacyId, notes);
      if (success) {
        setPharmacies(prev => 
          prev.map(p => 
            getPharmacyId(p) === pharmacyId 
              ? { ...p, isActive: false, isVerified: false, accountStatus: 'rejected' }
              : p
          )
        );
        toast.success('Pharmacy rejected successfully');
      } else {
        toast.error('Failed to reject pharmacy');
      }
    } catch (error) {
      console.error('Error rejecting pharmacy:', error);
      toast.error('Failed to reject pharmacy');
    }
  };

  const deletePharmacyHandler = async (pharmacy: Pharmacy) => {
    const pharmacyId = getPharmacyId(pharmacy);
    if (!pharmacyId) {
      toast.error('Invalid pharmacy ID');
      return;
    }

    try {
      const success = await deletePharmacy(pharmacyId);
      if (success) {
        setPharmacies(prev => prev.filter(p => getPharmacyId(p) !== pharmacyId));
        toast.success('Pharmacy deleted successfully');
      } else {
        toast.error('Failed to delete pharmacy');
      }
    } catch (error) {
      console.error('Error deleting pharmacy:', error);
      toast.error('Failed to delete pharmacy');
    }
  };

  const viewPharmacyDetails = (pharmacy: Pharmacy) => {
    const pharmacyId = getPharmacyId(pharmacy);
    if (pharmacyId) {
      navigate(`/admin/pharmacies/${pharmacyId}`);
    }
  };

  // Filter pharmacies for Account Management tab (exclude verified pharmacies - they go to Verified Pharmacies tab)
  const filteredAccountPharmacies = pharmacies.filter(pharmacy => {
    const matchesSearch = 
      (pharmacy.pharmacyName || pharmacy.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
      pharmacy.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pharmacy.phone.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    
    // When "all" is selected, show only non-verified pharmacies (pending, rejected, suspended)
    if (accountStatusFilter === "all") {
      matchesStatus = !(pharmacy.accountStatus === 'verified' || pharmacy.isVerified === true);
    } else if (accountStatusFilter === "pending") {
      matchesStatus = pharmacy.accountStatus === 'pending' || (!pharmacy.isVerified && pharmacy.accountStatus !== 'rejected' && pharmacy.accountStatus !== 'suspended');
    } else if (accountStatusFilter === "verified") {
      // This filter option allows seeing verified in account management if explicitly selected
      matchesStatus = pharmacy.accountStatus === 'verified' || pharmacy.isVerified === true;
    } else if (accountStatusFilter === "rejected") {
      matchesStatus = pharmacy.accountStatus === 'rejected';
    } else if (accountStatusFilter === "suspended") {
      matchesStatus = pharmacy.accountStatus === 'suspended';
    }
    
    return matchesSearch && matchesStatus;
  });

  // Filter pharmacies for Verified Pharmacies tab
  const filteredVerifiedPharmacies = pharmacies.filter(pharmacy => {
    const matchesSearch = 
      (pharmacy.pharmacyName || pharmacy.name || '').toLowerCase().includes(verifiedSearchTerm.toLowerCase()) || 
      pharmacy.email.toLowerCase().includes(verifiedSearchTerm.toLowerCase()) ||
      pharmacy.phone.toLowerCase().includes(verifiedSearchTerm.toLowerCase());
    
    return matchesSearch && (pharmacy.isVerified || pharmacy.accountStatus === 'verified');
  });

  // Pagination for Account Management
  const totalPagesAccounts = Math.ceil(filteredAccountPharmacies.length / itemsPerPage);
  const startIndexAccounts = (currentPageAccounts - 1) * itemsPerPage;
  const endIndexAccounts = startIndexAccounts + itemsPerPage;
  const paginatedAccountPharmacies = filteredAccountPharmacies.slice(startIndexAccounts, endIndexAccounts);

  // Pagination for Verified Pharmacies
  const totalPagesVerified = Math.ceil(filteredVerifiedPharmacies.length / itemsPerPage);
  const startIndexVerified = (currentPageVerified - 1) * itemsPerPage;
  const endIndexVerified = startIndexVerified + itemsPerPage;
  const paginatedVerifiedPharmacies = filteredVerifiedPharmacies.slice(startIndexVerified, endIndexVerified);

  function formatDate(dateString: string) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const getAccountStatusBadge = (pharmacy: Pharmacy) => {
    if (pharmacy.accountStatus === 'pending' || !pharmacy.isVerified) {
      return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    } else if (pharmacy.accountStatus === 'rejected') {
      return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
    } else if (pharmacy.accountStatus === 'verified' || pharmacy.isVerified) {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Verified</Badge>;
    } else if (pharmacy.accountStatus === 'suspended') {
      return <Badge className="bg-orange-100 text-orange-800"><XCircle className="w-3 h-3 mr-1" />Suspended</Badge>;
    }
    return null;
  };

  if (loading) {
    return (
      <GlobalLoader 
        isLoading={true} 
        message="Processing please wait..." 
        subMessage="Please wait" 
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          {/* <h1 className="text-3xl font-semibold text-gray-900">Pharmacy Network</h1> */}
          <p className="text-gray-600 mt-1">Manage and monitor pharmacy partners</p>
          {/* {pharmacyStats && (
            <div className="flex gap-4 mt-2 text-sm text-gray-500">
              <span>Total: {pharmacyStats.totalPharmacies || 0}</span>
              <span>Verified: {pharmacyStats.verifiedPharmacies || 0}</span>
              <span>Pending: {pharmacyStats.pendingPharmacies || 0}</span>
            </div>
          )} */}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger value="verified" 
          // className="flex items-center gap-2"
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            <Building2 className="w-4 h-4" />
            Verified Pharmacies
          </TabsTrigger>
          <TabsTrigger value="accounts" 
          // className="flex items-center gap-2"
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            <Users className="w-4 h-4" />
            Pharmacy Management
          </TabsTrigger>
          <TabsTrigger value="demo-requests" 
          // className="flex items-center gap-2"
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            <Calendar className="w-4 h-4" />
            Pharmacy to Demo
          </TabsTrigger>
        </TabsList>

        {/* Account Management Tab */}
        <TabsContent value="accounts" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search pharmacies..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-11"
                    />
                  </div>
                </div>
                {/* <div className="flex gap-4">
                  <Select value={accountStatusFilter} onValueChange={setAccountStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div> */}
              </div>
            </CardContent>
          </Card>

          {/* Pharmacies List */}
          <div className="space-y-4">
            {paginatedAccountPharmacies.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No pharmacies found</p>
                </CardContent>
              </Card>
            ) : (
              paginatedAccountPharmacies.map((pharmacy) => (
                <Card key={pharmacy.id || pharmacy._id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{pharmacy.pharmacyName || pharmacy.name || 'Unknown Pharmacy'}</h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-500 my-1">
                            <div className="flex items-center space-x-1">
                              <Mail className="w-4 h-4" />
                              <span>{pharmacy.email || 'N/A'}</span>
                            </div>
                            <div className="flex items-center space-x-1 my-1">
                              <Phone className="w-4 h-4" />
                              <span>{pharmacy.phone || 'N/A'}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-1 my-1 text-sm text-gray-500">
                            <MapPin className="w-4 h-4 mr-2" />
                            {
                              pharmacy.location === 'string' 
                              ? pharmacy.location 
                              : pharmacy.location?.address || ''
                            }
                          </div>                                                 
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        {/* <div className="text-center">
                          <p className="text-sm text-gray-500">Total Orders</p>
                          <p className="font-semibold">{pharmacy.totalOrders || 0}</p>
                        </div> */}
                        {/* <div className="text-center">
                          <p className="text-sm text-gray-500">Active Orders</p>
                          <p className="font-semibold">{pharmacy.activeOrders || 0}</p>
                        </div> */}
                        <div>
                          {getAccountStatusBadge(pharmacy)}
                        </div>
                        {pharmacy.accountStatus === 'pending' || !pharmacy.isVerified ? (
                          <div className="flex space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => approvePharmacyHandler(pharmacy)}
                              className="text-green-600 hover:text-green-700 border-green-600"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => rejectPharmacyHandler(pharmacy)}
                              className="text-red-600 hover:text-red-700 border-red-600"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <div className="flex space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => deletePharmacyHandler(pharmacy)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPagesAccounts > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {startIndexAccounts + 1} to {Math.min(endIndexAccounts, filteredAccountPharmacies.length)} of {filteredAccountPharmacies.length} pharmacies
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPageAccounts(prev => Math.max(prev - 1, 1))}
                  disabled={currentPageAccounts === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {currentPageAccounts} of {totalPagesAccounts}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPageAccounts(prev => Math.min(prev + 1, totalPagesAccounts))}
                  disabled={currentPageAccounts === totalPagesAccounts}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Verified Pharmacies Tab */}
        <TabsContent value="verified" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search verified pharmacies..."
                      value={verifiedSearchTerm}
                      onChange={(e) => setVerifiedSearchTerm(e.target.value)}
                      className="pl-10 h-11"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Verified Pharmacies List */}
          <div className="space-y-4">
            {paginatedVerifiedPharmacies.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No verified pharmacies found</p>
                </CardContent>
              </Card>
            ) : (
              paginatedVerifiedPharmacies.map((pharmacy) => (
                <Card 
                  key={pharmacy.id || pharmacy._id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => viewPharmacyDetails(pharmacy)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{pharmacy.pharmacyName || pharmacy.name || 'Unknown Pharmacy'}</h3>
                          <div className="flex items-center flex-wrap gap-4 text-sm text-gray-500 mt-1">
                            <div className="flex items-center justify-start my-1">
                              <div className="flex flex-row items-center space-x-1">
                                <div className="flex items-center space-x-1">
                                  <Mail className="w-4 h-4" />
                                <span>{pharmacy.email || 'N/A'}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Phone className="w-4 h-4" />
                                <span>{pharmacy.phone || 'N/A'}</span>
                              </div>
                            </div>
                          </div>                                              
                          </div>
                          <div className="flex items-center space-x-1 my-1 text-sm text-gray-500">
                            <MapPin className="w-4 h-4 mr-2" />
                            {
                              pharmacy.location === 'string' 
                              ? pharmacy.location 
                              : pharmacy.location?.address || ''
                            }
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-6">
                        {/* <div className="text-center">
                          <p className="text-sm text-gray-500">Total Orders</p>
                          <p className="text-2xl font-bold text-blue-600">{pharmacy.totalOrders || 0}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-500">Active Orders</p>
                          <p className="text-2xl font-bold text-green-600">{pharmacy.activeOrders || 0}</p>
                        </div> */}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            viewPharmacyDetails(pharmacy);
                          }}
                        >
                          <BarChart3 className="w-4 h-4 mr-1" />
                          View Analytics
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPagesVerified > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {startIndexVerified + 1} to {Math.min(endIndexVerified, filteredVerifiedPharmacies.length)} of {filteredVerifiedPharmacies.length} pharmacies
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPageVerified(prev => Math.max(prev - 1, 1))}
                  disabled={currentPageVerified === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {currentPageVerified} of {totalPagesVerified}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPageVerified(prev => Math.min(prev + 1, totalPagesVerified))}
                  disabled={currentPageVerified === totalPagesVerified}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Demo Requests Tab */}
        <TabsContent value="demo-requests" className="space-y-4">
          <DemoRequestsPage />
        </TabsContent>
      </Tabs>
    </div>
  );
}
